import React, { useState, useEffect, useCallback } from 'react';
import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    HStack,
    Heading,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Spinner,
    Switch,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Table,
    Tabs,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon, LockIcon } from '@chakra-ui/icons';
import { useAdminWorkspaces } from './hooks/useAdminWorkspaces';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';

/**
 * Platform administration: workspaces, access grants, and metadata.
 *
 * THE BOUNDARY THIS SCREEN MAKES VISIBLE (Steph's rule, 2026-08-03)
 * -----------------------------------------------------------------
 * An admin manages workspaces and users. An admin does NOT read conversations
 * or document contents. To read a workspace they create a user with access to
 * it -- and that access shows up here, as a row, with a date.
 *
 * The UI is built to make that boundary legible rather than merely enforced:
 *
 *   * The Documents and Conversations tabs say "metadata only" on their face,
 *     so an operator is never left wondering whether a missing preview is a bug.
 *   * Filenames and thread names are shown; there is no row action to open
 *     either, because the API returns no content to open.
 *   * Granting yourself access is possible and clearly labelled as visible --
 *     the point is that it leaves a trace, not that it is forbidden.
 *
 * A UI cannot leak what the API will not serve, which is why the backend was
 * built first (!30). This file's restraint is the second line, not the first.
 */

const formatBytes = (bytes) => {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const statusColour = (status) => {
    if (status === 'completed') return 'green';
    if (status === 'failed') return 'red';
    if (status === 'processing') return 'blue';
    return 'gray';
};

function WorkspaceFormModal({ isOpen, onClose, workspace, onSubmit }) {
    const isEdit = Boolean(workspace);
    const [slug, setSlug] = useState('');
    const [name, setName] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [busy, setBusy] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (!isOpen) return;
        setSlug(workspace?.slug || '');
        setName(workspace?.name || '');
        setIsActive(workspace ? workspace.is_active : true);
    }, [isOpen, workspace]);

    const submit = async () => {
        setBusy(true);
        try {
            await onSubmit(
                isEdit ? { name, is_active: isActive } : { slug, name }
            );
            onClose();
        } catch (error) {
            toast({
                title: isEdit ? 'Could not update workspace' : 'Could not create workspace',
                description: error.message,
                status: 'error',
                duration: 6000,
                isClosable: true,
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{isEdit ? 'Edit workspace' : 'New workspace'}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <FormControl>
                            <FormLabel>Slug</FormLabel>
                            <Input
                                value={slug}
                                onChange={(e) => setSlug(e.target.value)}
                                placeholder="races-supermoto"
                                isDisabled={isEdit}
                            />
                            <FormHelperText>
                                {isEdit
                                    ? 'The slug cannot be changed: it is used in configuration and by the instance-merge tooling, so renaming it would break an existing mapping.'
                                    : 'Lowercase letters, digits and hyphens. Permanent once created.'}
                            </FormHelperText>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Name</FormLabel>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Supermoto races"
                            />
                        </FormControl>

                        {isEdit && (
                            <FormControl display="flex" alignItems="center">
                                <FormLabel mb="0">Active</FormLabel>
                                <Switch
                                    isChecked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                />
                                <FormHelperText ml={3} mb={0}>
                                    Inactive workspaces disappear from the switcher.
                                    Content is kept.
                                </FormHelperText>
                            </FormControl>
                        )}

                        {!isEdit && (
                            <Alert status="info" fontSize="sm" borderRadius="md">
                                <AlertIcon />
                                Creating a workspace grants nobody access to it —
                                including you. Add a member afterwards; the grant is
                                recorded and visible.
                            </Alert>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme="blue"
                        onClick={submit}
                        isLoading={busy}
                        isDisabled={!name || (!isEdit && !slug)}
                    >
                        {isEdit ? 'Save' : 'Create'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

function MembersModal({ isOpen, onClose, workspace, admin }) {
    const { users, fetchMembers, addMember, updateMemberRole, removeMember } = admin;
    const { user: currentUser } = useAuth();
    // Granting or revoking YOUR OWN membership changes which surfaces the app
    // may show -- WorkspaceGate keys off this list. The modal's own `reload`
    // below only refetches this member table, so without refetching the
    // app-level list too, an admin who has just unlocked themselves still sees
    // "you are not a member of any workspace" until they press F5. Found by
    // David reviewing !47; `reload` had no caller anywhere in the app before.
    const { reload: reloadAppWorkspaces } = useWorkspace();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [role, setRole] = useState('member');
    const toast = useToast();

    const reload = useCallback(async () => {
        if (!workspace) return;
        setLoading(true);
        setMembers(await fetchMembers(workspace.id));
        setLoading(false);
    }, [workspace, fetchMembers]);

    useEffect(() => {
        if (isOpen) reload();
    }, [isOpen, reload]);

    const memberIds = new Set(members.map((m) => m.user_id));
    const candidates = users.filter((u) => !memberIds.has(u.id));

    const grant = async () => {
        try {
            const grantedUserId = Number(selectedUserId);
            await addMember(workspace.id, {
                userId: grantedUserId,
                role,
            });
            setSelectedUserId('');
            setRole('member');
            await reload();
            // Read from the captured id, not selectedUserId: it has just been
            // reset to '' above, and Number('') is 0.
            if (grantedUserId === currentUser?.id) await reloadAppWorkspaces();
        } catch (error) {
            toast({
                title: 'Could not grant access',
                description: error.message,
                status: 'error',
                duration: 6000,
                isClosable: true,
            });
        }
    };

    const revoke = async (userId) => {
        try {
            await removeMember(workspace.id, userId);
            await reload();
            // Symmetric case: revoking your own last membership must drop you
            // into the banner state rather than leave a stale UI offering Chat
            // that would 403 on the first send.
            if (userId === currentUser?.id) await reloadAppWorkspaces();
        } catch (error) {
            toast({
                title: 'Could not revoke access',
                description: error.message,
                status: 'error',
                duration: 6000,
                isClosable: true,
            });
        }
    };

    const changeRole = async (userId, nextRole) => {
        try {
            await updateMemberRole(workspace.id, userId, nextRole);
            await reload();
        } catch (error) {
            toast({
                title: 'Could not change role',
                description: error.message,
                status: 'error',
                duration: 6000,
                isClosable: true,
            });
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    Access — {workspace?.name}
                    <Text fontSize="sm" fontWeight="normal" color="gray.500">
                        Who can read this workspace. Every grant is a dated row.
                    </Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        {loading ? (
                            <Flex justify="center" py={6}><Spinner /></Flex>
                        ) : members.length === 0 ? (
                            <Alert status="warning" fontSize="sm" borderRadius="md">
                                <AlertIcon />
                                Nobody can read this workspace yet.
                            </Alert>
                        ) : (
                            <Table size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>User</Th>
                                        <Th>Role</Th>
                                        <Th>Granted</Th>
                                        <Th />
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {members.map((member) => (
                                        <Tr key={member.user_id}>
                                            <Td>
                                                {member.username}
                                                {member.user_id === currentUser?.id && (
                                                    <Badge ml={2} colorScheme="purple">
                                                        you
                                                    </Badge>
                                                )}
                                            </Td>
                                            <Td>
                                                <Select
                                                    size="sm"
                                                    width="120px"
                                                    value={member.role}
                                                    onChange={(e) =>
                                                        changeRole(
                                                            member.user_id,
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option value="member">member</option>
                                                    <option value="admin">admin</option>
                                                </Select>
                                            </Td>
                                            <Td fontSize="sm" color="gray.500">
                                                {formatDate(member.created_at)}
                                            </Td>
                                            <Td>
                                                <Tooltip label="Revoke access — the workspace keeps its content">
                                                    <IconButton
                                                        aria-label="Revoke access"
                                                        icon={<DeleteIcon />}
                                                        size="sm"
                                                        variant="ghost"
                                                        colorScheme="red"
                                                        onClick={() => revoke(member.user_id)}
                                                    />
                                                </Tooltip>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        )}

                        <Box borderTopWidth="1px" pt={4}>
                            <FormLabel fontSize="sm">Grant access</FormLabel>
                            <HStack>
                                <Select
                                    placeholder="Select user"
                                    value={selectedUserId}
                                    onChange={(e) => setSelectedUserId(e.target.value)}
                                >
                                    {candidates.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.username}
                                            {u.id === currentUser?.id ? ' (you)' : ''}
                                        </option>
                                    ))}
                                </Select>
                                <Select
                                    width="140px"
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                >
                                    <option value="member">member</option>
                                    <option value="admin">admin</option>
                                </Select>
                                <Button
                                    colorScheme="blue"
                                    onClick={grant}
                                    isDisabled={!selectedUserId}
                                    minW="90px"
                                >
                                    Grant
                                </Button>
                            </HStack>
                            {String(selectedUserId) === String(currentUser?.id) && (
                                <Alert status="info" fontSize="sm" mt={3} borderRadius="md">
                                    <AlertIcon />
                                    You are granting yourself access to this
                                    workspace. That is allowed — and it will be
                                    recorded here for anyone to see.
                                </Alert>
                            )}
                        </Box>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose}>Close</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

function MetadataOnlyNotice({ what }) {
    return (
        <Alert status="info" fontSize="sm" borderRadius="md" mb={4}>
            <AlertIcon as={LockIcon} />
            <Box>
                <Text fontWeight="semibold">Metadata only</Text>
                <Text>
                    Administration shows which {what} exist, never their contents.
                    To read {what}, use an account with access to the workspace —
                    that access is recorded as a membership.
                </Text>
            </Box>
        </Alert>
    );
}

function WorkspaceManagement() {
    const admin = useAdminWorkspaces();
    const {
        workspaces,
        isLoading,
        createWorkspace,
        updateWorkspace,
        deleteWorkspace,
        fetchDocumentsMetadata,
        fetchConversationsMetadata,
    } = admin;

    const formModal = useDisclosure();
    const membersModal = useDisclosure();
    const [editing, setEditing] = useState(null);
    const [membersFor, setMembersFor] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [conversations, setConversations] = useState([]);
    const toast = useToast();

    const openCreate = () => {
        setEditing(null);
        formModal.onOpen();
    };

    const openEdit = (workspace) => {
        setEditing(workspace);
        formModal.onOpen();
    };

    const openMembers = (workspace) => {
        setMembersFor(workspace);
        membersModal.onOpen();
    };

    const submitForm = async (values) =>
        editing ? updateWorkspace(editing.id, values) : createWorkspace(values);

    const confirmDelete = async (workspace) => {
        // The server refuses to delete a workspace holding content (409) -- an
        // admin may not destroy what it may not read. Deactivation is the normal
        // path, so steer there rather than presenting deletion as routine.
        const hasContent =
            workspace.conversation_count > 0 || workspace.document_count > 0;
        if (hasContent) {
            toast({
                title: 'Cannot delete a workspace with content',
                description:
                    `${workspace.name} holds ${workspace.conversation_count} ` +
                    `conversation(s) and ${workspace.document_count} document(s). ` +
                    'Deactivate it instead, or have a member remove the content first.',
                status: 'warning',
                duration: 8000,
                isClosable: true,
            });
            return;
        }
        if (!window.confirm(`Delete empty workspace “${workspace.name}”?`)) return;
        try {
            await deleteWorkspace(workspace.id);
            toast({ title: 'Workspace deleted', status: 'success', duration: 3000 });
        } catch (error) {
            toast({
                title: 'Could not delete workspace',
                description: error.message,
                status: 'error',
                duration: 8000,
                isClosable: true,
            });
        }
    };

    const loadDocuments = useCallback(async () => {
        setDocuments(await fetchDocumentsMetadata());
    }, [fetchDocumentsMetadata]);

    const loadConversations = useCallback(async () => {
        setConversations(await fetchConversationsMetadata());
    }, [fetchConversationsMetadata]);

    return (
        <Box p={6} maxW="1200px" mx="auto">
            <Flex justify="space-between" align="center" mb={2}>
                <Heading size="lg">Workspaces</Heading>
                <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={openCreate}>
                    New workspace
                </Button>
            </Flex>
            <Text color="gray.500" mb={6}>
                Administration manages workspaces, users and access. It does not
                grant sight of anyone's conversations or documents.
            </Text>

            <Tabs colorScheme="blue" isLazy>
                <TabList>
                    <Tab>Workspaces</Tab>
                    <Tab onClick={loadDocuments}>Documents</Tab>
                    <Tab onClick={loadConversations}>Conversations</Tab>
                </TabList>

                <TabPanels>
                    <TabPanel px={0}>
                        {isLoading ? (
                            <Flex justify="center" py={10}><Spinner /></Flex>
                        ) : workspaces.length === 0 ? (
                            <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                No workspaces yet.
                            </Alert>
                        ) : (
                            <Table size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>Workspace</Th>
                                        <Th>Slug</Th>
                                        <Th isNumeric>Members</Th>
                                        <Th isNumeric>Threads</Th>
                                        <Th isNumeric>Docs</Th>
                                        <Th>Keys</Th>
                                        <Th>Status</Th>
                                        <Th />
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {workspaces.map((workspace) => (
                                        <Tr key={workspace.id}>
                                            <Td fontWeight="medium">{workspace.name}</Td>
                                            <Td>
                                                <Text as="code" fontSize="sm">
                                                    {workspace.slug}
                                                </Text>
                                            </Td>
                                            <Td isNumeric>
                                                {workspace.member_count === 0 ? (
                                                    <Tooltip label="Nobody can read this workspace">
                                                        <Badge colorScheme="orange">0</Badge>
                                                    </Tooltip>
                                                ) : (
                                                    workspace.member_count
                                                )}
                                            </Td>
                                            <Td isNumeric>{workspace.conversation_count}</Td>
                                            <Td isNumeric>{workspace.document_count}</Td>
                                            <Td>
                                                {workspace.credential_providers.length === 0 ? (
                                                    <Text fontSize="sm" color="gray.400">
                                                        —
                                                    </Text>
                                                ) : (
                                                    <HStack spacing={1}>
                                                        {workspace.credential_providers.map(
                                                            (provider) => (
                                                                <Badge
                                                                    key={provider}
                                                                    colorScheme="green"
                                                                >
                                                                    {provider}
                                                                </Badge>
                                                            )
                                                        )}
                                                    </HStack>
                                                )}
                                            </Td>
                                            <Td>
                                                <Badge
                                                    colorScheme={
                                                        workspace.is_active ? 'green' : 'gray'
                                                    }
                                                >
                                                    {workspace.is_active
                                                        ? 'active'
                                                        : 'inactive'}
                                                </Badge>
                                            </Td>
                                            <Td>
                                                <HStack spacing={1} justify="flex-end">
                                                    <Button
                                                        size="xs"
                                                        onClick={() => openMembers(workspace)}
                                                    >
                                                        Access
                                                    </Button>
                                                    <IconButton
                                                        aria-label="Edit workspace"
                                                        icon={<EditIcon />}
                                                        size="xs"
                                                        variant="ghost"
                                                        onClick={() => openEdit(workspace)}
                                                    />
                                                    <Tooltip
                                                        label={
                                                            workspace.conversation_count > 0 ||
                                                            workspace.document_count > 0
                                                                ? 'Holds content — deactivate instead'
                                                                : 'Delete empty workspace'
                                                        }
                                                    >
                                                        <IconButton
                                                            aria-label="Delete workspace"
                                                            icon={<DeleteIcon />}
                                                            size="xs"
                                                            variant="ghost"
                                                            colorScheme="red"
                                                            onClick={() =>
                                                                confirmDelete(workspace)
                                                            }
                                                        />
                                                    </Tooltip>
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        )}
                    </TabPanel>

                    <TabPanel px={0}>
                        <MetadataOnlyNotice what="documents" />
                        {documents.length === 0 ? (
                            <Text color="gray.500">No documents.</Text>
                        ) : (
                            <Table size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>Filename</Th>
                                        <Th>Workspace</Th>
                                        <Th isNumeric>Size</Th>
                                        <Th>Status</Th>
                                        <Th>Tier</Th>
                                        <Th>Created</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {documents.map((document) => (
                                        <Tr key={document.id}>
                                            <Td>{document.filename}</Td>
                                            <Td>
                                                <Text as="code" fontSize="sm">
                                                    {document.workspace_slug}
                                                </Text>
                                            </Td>
                                            <Td isNumeric>
                                                {formatBytes(document.file_size)}
                                            </Td>
                                            <Td>
                                                <Badge
                                                    colorScheme={statusColour(
                                                        document.status
                                                    )}
                                                >
                                                    {document.status}
                                                </Badge>
                                            </Td>
                                            <Td fontSize="sm">{document.ocr_tier}</Td>
                                            <Td fontSize="sm" color="gray.500">
                                                {formatDate(document.created_at)}
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        )}
                    </TabPanel>

                    <TabPanel px={0}>
                        <MetadataOnlyNotice what="conversations" />
                        {conversations.length === 0 ? (
                            <Text color="gray.500">No conversations.</Text>
                        ) : (
                            <Table size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>Thread</Th>
                                        <Th>Workspace</Th>
                                        <Th isNumeric>Turns</Th>
                                        <Th>Last activity</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {conversations.map((conversation) => (
                                        <Tr key={conversation.conversation_id}>
                                            <Td>{conversation.name}</Td>
                                            <Td>
                                                <Text as="code" fontSize="sm">
                                                    {conversation.workspace_slug}
                                                </Text>
                                            </Td>
                                            <Td isNumeric>{conversation.turn_count}</Td>
                                            <Td fontSize="sm" color="gray.500">
                                                {formatDate(conversation.last_activity)}
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        )}
                    </TabPanel>
                </TabPanels>
            </Tabs>

            <WorkspaceFormModal
                isOpen={formModal.isOpen}
                onClose={formModal.onClose}
                workspace={editing}
                onSubmit={submitForm}
            />
            <MembersModal
                isOpen={membersModal.isOpen}
                onClose={membersModal.onClose}
                workspace={membersFor}
                admin={admin}
            />
        </Box>
    );
}

export default WorkspaceManagement;
